import { useState, useEffect, useCallback, useRef } from 'react';
import { logger } from 'src/logger';

type StorageType = 'localStorage' | 'sessionStorage';

interface PersistOptions {
  storage?: StorageType;
  ttl?: number; // Time to live in milliseconds
  clearOnRetrieve?: boolean; // Clear data after retrieving (one-time use)
}

interface StoredData<T> {
  value: T;
  timestamp: number;
  ttl?: number;
}

/**
 * Hook for persisting state with TTL and storage options
 * Scalable for future use cases like campaign pre-fill and Rodeo integration
 */
export function usePersistState<T>(
  key: string,
  defaultValue: T,
  options: PersistOptions = {},
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  const { storage = 'localStorage', ttl, clearOnRetrieve = false } = options;

  // Get storage instance immediately
  const getStorage = useCallback((): Storage | null => {
    try {
      // Check for SSR environment
      if (typeof window === 'undefined') {
        return null;
      }
      return storage === 'localStorage' ? localStorage : sessionStorage;
    } catch (error) {
      logger.warn('usePersistState: Storage not available', { storage, error });
      return null;
    }
  }, [storage]);

  // Get stored value with TTL check
  const getStoredValue = useCallback((): T => {
    const storageInstance = getStorage();
    if (!storageInstance) return defaultValue;

    try {
      const stored = storageInstance.getItem(key);
      if (!stored) return defaultValue;

      const data: StoredData<T> = JSON.parse(stored);

      // Validate timestamp
      if (typeof data.timestamp !== 'number' || isNaN(data.timestamp) || data.timestamp < 0) {
        storageInstance.removeItem(key);
        return defaultValue;
      }

      // Check TTL expiration
      if (data.ttl && Date.now() - data.timestamp > data.ttl) {
        storageInstance.removeItem(key);
        return defaultValue;
      }

      // Clear on retrieve if requested
      if (clearOnRetrieve) {
        storageInstance.removeItem(key);
      }

      return data.value;
    } catch (error) {
      logger.warn('usePersistState: Failed to parse stored data', { key, error });
      return defaultValue;
    }
  }, [key, defaultValue, clearOnRetrieve, getStorage]);

  const [state, setState] = useState<T>(() => getStoredValue());

  // Store value with timestamp and TTL
  const setStoredValue = useCallback(
    (value: T) => {
      const storageInstance = getStorage();
      if (!storageInstance) return;

      try {
        const data: StoredData<T> = {
          value,
          timestamp: Date.now(),
          ...(ttl && { ttl }),
        };

        storageInstance.setItem(key, JSON.stringify(data));
      } catch (error) {
        logger.warn('usePersistState: Failed to store data', { key, error });
      }
    },
    [key, ttl, getStorage],
  );

  // Enhanced setState that persists to storage
  const setPersistState = useCallback(
    (value: T | ((prev: T) => T)) => {
      setState((prevState) => {
        const newValue = typeof value === 'function' ? (value as (prev: T) => T)(prevState) : value;
        setStoredValue(newValue);
        return newValue;
      });
    },
    [setStoredValue],
  );

  // Clear stored data
  const clearStoredValue = useCallback(() => {
    const storageInstance = getStorage();
    if (!storageInstance) return;

    try {
      storageInstance.removeItem(key);
      setState(defaultValue);
    } catch (error) {
      logger.warn('usePersistState: Failed to clear data', { key, error });
    }
  }, [key, defaultValue, getStorage]);

  return [state, setPersistState, clearStoredValue];
}
